import {Component, OnDestroy, OnInit} from '@angular/core';
import {BreadcrumbModel} from '@shared/models/breadcrumb.model';
import {Observable, Subject} from 'rxjs';
import {InvalidateSubject} from '@shared/helpers/invalidate.subject';
import {ActivatedRoute, Router} from '@angular/router';
import {DialogService} from '@shared/services/dialog-service';
import {CommandService} from '@core/services/command.service';
import {select, Store} from '@ngrx/store';
import {DataSurveyState} from '../../store/reducers/data-survey.reducers';
import {ParticleService} from '@core/services/particle.service';
import {take, takeUntil} from 'rxjs/operators';
import {
  AddFieldInFormCommand,
  AddSectionInFormCommand,
  ChangeViewTypeCommand, ConvertSectionsToTabsCommand, ConvertTabsToSectionsCommand,
  DeleteSectionFromFormCommand, RenameSectionInFormCommand
} from '../../commands/survey.commands';
import {IQueryState} from '@shared/store/reducers/query.reducer';
import {getQueryResponse} from '@shared/store/selectors/query.selector';
import {QueryProvider} from '@shared/queries/query-provider';
import {DynamicFieldItem} from '@shared/components/dynamic-form/dynamic-form-item';
import {QueryModel} from '@shared/models/query-model';
import {DeleteQueryAction} from '@shared/store/actions/query.actions';
import {Guid} from 'guid-typescript';
import {FieldEditorDialogModel} from '@shared/models/field-editor.model';
import {FieldDefinition} from '@shared/models/field-definition';
import {DialogModel} from '@shared/models/dialog.model';
import {Configuration} from '@shared/models/configuration';
import {SmartGridService} from '@core/services/smart-grid.service';

@Component({
    standalone: false,
    selector: 'particle-data-survey-form-designer',
  templateUrl: './data-survey-form-designer.component.html',
  styleUrls: ['./data-survey-form-designer.component.scss']
})
export class DataSurveyFormDesignerComponent implements OnInit, OnDestroy {

  breadcrumbs: BreadcrumbModel[];
  unsubscribe$: Subject<void> = new Subject<void>();
  invalidateSubject: InvalidateSubject;
  private surveyFormModel$: Observable<any>;
  private surveyFormId: string;
  containerClass: string;
  formField: DynamicFieldItem;
  sampleItem: any;
  fieldConfig: any;
  item: any;
  sourceType: string;
  endpointName: string;
  showDelete: boolean;
  showLanguageOption: boolean;
  editedFields: any [] = [];
  queryModels: QueryModel[] = [];
  fieldSection: any;
  constructor(private router: Router,
              private dialogService: DialogService,
              private commandService: CommandService,
              private dataSurveyStore: Store<DataSurveyState>,
              private activatedRoute: ActivatedRoute,
              private queryStore: Store<IQueryState>,
              private smartGridService: SmartGridService,
              private particleService: ParticleService) {
    this.createBreadcrumbs(null);
    this.containerClass = 'content';
    this.showDelete = false;
    this.showLanguageOption = false;
    this.endpointName = 'DataSurveyApi';
    this.invalidateSubject = new InvalidateSubject();
  }

  private createBreadcrumbs(item) {
    this.breadcrumbs = [
      <BreadcrumbModel>{
        id: 'DataSurvey',
        title: 'Data Surveys',
        type: 'DataSurvey',
        relativeUrl: 'data-survey'
      },
      <BreadcrumbModel>{
        id: 'DataSurveyForm',
        title: 'Forms',
        type: 'DataSurveyForm',
        relativeUrl: 'data-survey/forms'
      }
    ];
    if (item) {
      this.breadcrumbs.push(<BreadcrumbModel>{
        id: 'DataSurveyFormItem',
        title: item.cells['Title'],
        type: 'DataSurveyFormItem',
        relativeUrl: `data-survey/forms/design/${item.id}`
      });
    }
  }

  ngOnInit() {
    this.surveyFormModel$ = this.queryStore.pipe(select(getQueryResponse, 'SurveyFormItemById'));
    this.surveyFormModel$.pipe(takeUntil(this.unsubscribe$)).subscribe(state => {
      console.log('SurveyFormItemById', state);
      if (state) {
        if (this.formField) {
          this.transferExpandedStte(this.formField, state.formField);
        }
        this.formField = null;

        const self = this;
        setTimeout(() => {
          self.formField = state.formField;
          self.sampleItem = state.sampleItem;
          self.item = state.item;
          self.fieldConfig = state.fieldConfig;
          self.sourceType = self.surveyFormId;
          self.fieldSection = state.fieldSection;
          if (self.formField.viewType === 'Tab') {
            self.containerClass = 'content';
          } else {
            self.containerClass = 'content m-30';
          }
          self.createBreadcrumbs(state.item);
        }, 10);
      }
    });
    this.activatedRoute.params.subscribe((params: any) => {
      this.surveyFormId = params.formId;
      this.queryModels.push(QueryProvider.getQuery('SurveyFormItemById'));
      QueryProvider.invokeQuery(this.queryStore, 'SurveyFormItemById', {'[Id]': this.surveyFormId });
    });
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.queryModels.forEach(c => {
      this.queryStore.dispatch(new DeleteQueryAction(c, null, null));
    });
  }

  addSectionInRoot() {
    this.addSection(this.formField, null);
  }

  onValueChanged($event: any) {
    console.log('onValueChanged in designer', $event);
    switch ($event.action) {
      case 'Rename':
        this.rename($event.formField);
        break;
      case 'AddSection':
        this.addSection($event.formField, null);
        break;
      case 'Collapsible':
        this.changeViewType($event.formField, 'Collapsible');
        break;
      case 'AddField':
        this.addField($event.formField, null);
        break;
      case 'AddChildField':
        this.addField($event.formField, $event.fieldDefinition.key);
        break;
      case 'AddChildSection':
        this.addSection($event.formField, $event.fieldDefinition.key);
        break;
      case 'MoveUp':
        break;
      case 'MoveDown':
        break;
      case 'DeleteSection':
        this.deleteSection($event.formField);
        break;
      case 'NonCollapsible':
        this.changeViewType($event.formField, 'Group');
        break;
      case 'MoveLeft':
        break;
      case 'MoveRight':
        break;
      case 'DeleteTab':
        this.deleteSection($event.formField);
        break;
    }
  }

  addField(formField: any, parentFieldKey: string) {
    let smartGridConfig = Configuration.getSmartGridConfig(this.surveyFormId);
    if (!smartGridConfig) {
      smartGridConfig = {
        'RootUrlKey': 'DataSurveyApi',
        'SourceType': this.surveyFormId,
        'IsCompanySpecific': true,
        'ItemsUrlIndex': 0,
        'Parameters': {}
      };
      Configuration.setSmartGridConfig(this.surveyFormId, smartGridConfig);
    }
    const editFieldDetails = new FieldEditorDialogModel();

    const fieldDefinition = new FieldDefinition();
    fieldDefinition.key = Guid.create().toString();
    editFieldDetails.fieldDefinition = fieldDefinition;
    editFieldDetails.clientConfig = this.surveyFormId;
    editFieldDetails.selectedSection = JSON.parse(JSON.stringify(this.fieldSection));
    editFieldDetails.hideSettings = true;
    editFieldDetails.editOrAddSectionAllowed = false;
    const dialogModel: DialogModel = DialogModel.create(
      'Add Field',
      'FieldEditorDialogComponent', { 'editFieldDetails': editFieldDetails},
      '700px',
      '700px'
    );
    this.dialogService.showOkCancelDialog(dialogModel).subscribe(res => {
      if (res.action === 'OK') {
        console.log(res);
        this.smartGridService.addField(this.sourceType, res.data.input.editFieldDetails.selectedSection.id, res.data.input.editFieldDetails.fieldDefinition)
          .subscribe(state => {
          console.log('addField', state);
          if (state === fieldDefinition.key) {
            const command = new AddFieldInFormCommand(this.surveyFormId, formField.headerKey, res.data.input.editFieldDetails.fieldDefinition.key, parentFieldKey);
            this.commandService.executeCommand(command).pipe(take(1)).subscribe(commandState => {
              QueryProvider.invokeQuery(this.queryStore, 'SurveyFormItemById', {'[Id]': this.surveyFormId });
            });
          }
        });
      }
    });
  }

  private changeViewType(formField: any, viewType: string) {
    const command = new ChangeViewTypeCommand(this.surveyFormId, formField.headerKey, viewType);
    this.commandService.executeCommand(command).pipe(take(1)).subscribe(commandState => {
      QueryProvider.invokeQuery(this.queryStore, 'SurveyFormItemById', {'[Id]': this.surveyFormId });
    });
  }

  private addSection(formField: any, parentFieldKey) {
    this.dialogService.showTextInputDialog('Add Section', '', '', 100, 'Title')
      .pipe(take(1)).subscribe(state => {
        if (state.action === 'OK') {
          const command = new AddSectionInFormCommand(this.surveyFormId, formField.headerKey, parentFieldKey, 'Group', state.data.input.text);
          this.commandService.executeCommand(command).pipe(take(1)).subscribe(commandState => {
            QueryProvider.invokeQuery(this.queryStore, 'SurveyFormItemById', {'[Id]': this.surveyFormId });
          });
        }

    });
  }

  private deleteSection(formField: any) {
    this.dialogService.showYesNoDialog('Confirm Delete', 'Are you sure to delete?').pipe(take(1)).subscribe(state => {
      if (state.action === 'OK') {
        const command = new DeleteSectionFromFormCommand(this.surveyFormId, formField.headerKey);
        this.commandService.executeCommand(command).pipe(take(1)).subscribe(commandState => {
          QueryProvider.invokeQuery(this.queryStore, 'SurveyFormItemById', {'[Id]': this.surveyFormId });
        });
      }
    });
  }

  private transferExpandedStte(sourceFormField: DynamicFieldItem, destinationformField: DynamicFieldItem) {
    if (sourceFormField.headerKey === destinationformField.headerKey) {
      destinationformField.isExpanded = sourceFormField.isExpanded;
    }
    sourceFormField.children.forEach(sc => {
      const dc = destinationformField.children.find(o => o.headerKey === sc.headerKey);
      if (dc) {
        this.transferExpandedStte(sc, dc);
      }
    });
  }

  private rename(formField: DynamicFieldItem) {
    this.dialogService.showTextInputDialog('Rename', formField.header, '', 100, 'Title')
      .pipe(take(1)).subscribe(state => {
      if (state.action === 'OK') {
        const command = new RenameSectionInFormCommand(this.surveyFormId, formField.headerKey, state.data.input.text);
        this.commandService.executeCommand(command).pipe(take(1)).subscribe(commandState => {
          QueryProvider.invokeQuery(this.queryStore, 'SurveyFormItemById', {'[Id]': this.surveyFormId });
        });
      }
    });
  }

  convertTabsToSections() {
    const command = new ConvertTabsToSectionsCommand(this.surveyFormId);
    this.commandService.executeCommand(command).pipe(take(1)).subscribe(commandState => {
      QueryProvider.invokeQuery(this.queryStore, 'SurveyFormItemById', {'[Id]': this.surveyFormId });
    });
  }

  convertSectionsToTabs() {
    const command = new ConvertSectionsToTabsCommand(this.surveyFormId);
    this.commandService.executeCommand(command).pipe(take(1)).subscribe(commandState => {
      QueryProvider.invokeQuery(this.queryStore, 'SurveyFormItemById', {'[Id]': this.surveyFormId });
    });
  }
}
